import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HomeModernIcon,
  VideoCameraIcon,
  SignalIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { heroToFarmSlide } from '../../../utils/homeFarmPreview';
import { isCameraType, TYPE_CAMERA } from '../../../components/farm-smart-device/cameraDeviceType';
import {
  isHikvisionCamera,
  parseHikvisionConfig,
  isCameraConfigured,
} from '../../../components/farm-smart-device/hikvisionCameraDevice';
import { processImageUrl } from '../../../utils/imageUtils';
import { buildSmartDeviceUrl, SMART_DEVICE_TABS } from '../../../utils/smartDeviceNav';
import { buildBusinessContextPath, EMPTY_COPY } from '../../../utils/businessContextNav';

const MAX_CAMERA_CARDS = 4;
const AUTOPLAY_MS = 5000;

/**
 * 在线判定：库字段 online_status 为 Boolean，不能先 String() 再比 ONLINE
 * （String(true)==="TRUE" 会被误判离线）。
 */
function deviceOnline(d) {
  if (!d || typeof d !== 'object') return false;
  if (d.onlineStatus === true || d.onlineStatus === 1) return true;
  if (d.onlineStatus === false || d.onlineStatus === 0) return false;
  if (d.online === true || d.isOnline === true) return true;
  if (d.online === false || d.isOnline === false) return false;
  const s = String(d.onlineStatus ?? d.status ?? '')
    .trim()
    .toUpperCase();
  if (!s) return false;
  if (s === 'ONLINE' || s === '1' || s === 'ON' || s === 'TRUE' || s === '在线') return true;
  if (s === 'OFFLINE' || s === '0' || s === 'FALSE' || s === 'OFF' || s === '离线') return false;
  return false;
}

function deviceDisplayName(d) {
  return d?.deviceName || d?.name || d?.deviceCode || d?.id || '摄像头';
}

/** 静态封面：兼容多字段历史图 */
function resolveStaticCover(cam) {
  const raw =
    cam?.imageUrl ||
    cam?.coverUrl ||
    cam?.snapshotUrl ||
    cam?.previewUrl ||
    cam?.lastImageUrl ||
    cam?.thumbnailUrl ||
    '';
  return processImageUrl(raw) || '';
}

function mapOperationSlotToSlide(slot) {
  return {
    kind: 'operation',
    id: slot.id,
    coverUrl: processImageUrl(slot.imageUrl) || '',
    name: slot.title || '运营位',
    subtitle: slot.subtitle || '',
    linkUrl: String(slot.linkUrl || '').trim(),
    linkTarget: slot.linkTarget || '_self',
    badgeText: '推荐',
    status: '运营',
    area: '—',
    crop: '—',
    plots: '—',
    progress: 0,
  };
}

/**
 * 农场总览轮播：优先展示「首页运营位」后台配置的轮播（原独立横幅已下线），
 * 无运营轮播时回退农场档案轮播；下方保留田间监控。
 * 运营位与设备列表由 WelcomePage 单次加载后经 props 传入。
 */
const FarmOverviewCard = ({
  farmPreview,
  loading,
  carouselSlots = [],
  opLoading = false,
  smartDevices = [],
  // devicesLoading reserved; devices arrive via silent channel without spinner
  devicesLoading: _devicesLoading = false,
  landId: landIdProp = '',
}) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  const hero = farmPreview?.hero || null;
  const isDemo = farmPreview?.showDemoFarmPreview === true;
  const hasFarm = Boolean(hero && !hero.isDemo);
  /** 摄像头始终绑定主农场，与运营轮播解耦 */
  const activeFarmId = String(farmPreview?.preferredFarmId || hero?.farmId || '').trim();

  const farmSlides = useMemo(() => {
    const slides = Array.isArray(farmPreview?.farmSlides) ? farmPreview.farmSlides : [];
    if (slides.length > 0) {
      return slides.map((s) => ({ ...s, kind: 'farm' }));
    }
    if (farmPreview?.hero) {
      const slide = heroToFarmSlide(farmPreview.hero);
      return slide ? [{ ...slide, kind: 'farm' }] : [];
    }
    return [];
  }, [farmPreview]);

  const opSlots = useMemo(
    () => (Array.isArray(carouselSlots) ? carouselSlots : []).map(mapOperationSlotToSlide),
    [carouselSlots]
  );

  const cameras = useMemo(() => {
    if (isDemo) return [];
    const cams = (Array.isArray(smartDevices) ? smartDevices : []).filter((d) =>
      isCameraType(d?.deviceType)
    );
    if (!cams.length) return [];
    const byFarm = activeFarmId
      ? cams.filter(
          (d) =>
            String(d?.farmId || d?.farm_id || '').trim() === activeFarmId
        )
      : [];
    const list = byFarm.length ? byFarm : cams;

    // 海康多通道：config.channels 展开为多路卡片，避免通道二有设备但无独立卡片
    const expanded = [];
    for (const d of list) {
      const hik = isHikvisionCamera(d) || isCameraConfigured(d);
      if (!hik) {
        expanded.push({
          ...d,
          _cardKey: String(d.id || d.deviceId || ''),
          _channelNo: null,
        });
        continue;
      }
      const cfg = parseHikvisionConfig(d);
      const channels =
        Array.isArray(cfg.channels) && cfg.channels.length
          ? cfg.channels
          : [cfg.channelNo > 0 ? cfg.channelNo : 1];
      channels.forEach((ch) => {
        expanded.push({
          ...d,
          _cardKey: `${d.id}:${ch}`,
          _channelNo: ch,
        });
      });
    }
    return expanded.slice(0, MAX_CAMERA_CARDS);
  }, [smartDevices, activeFarmId, isDemo]);

  // 摄像头用静默下发的设备列表，无独立 loading（避免首屏假忙）
  const cameraLoading = false;

  const slides = useMemo(() => {
    if (opSlots.length > 0) return opSlots;
    return farmSlides;
  }, [opSlots, farmSlides]);

  const usingOperationCarousel = opSlots.length > 0;

  /**
   * 有运营轮播时不依赖农场聚合 loading（多接口较慢），配置接口返回即可展示。
   * 仅在没有运营轮播、只能回退农场封面时，才等待 farmPreview。
   */
  const carouselBusy =
    opLoading || (!usingOperationCarousel && loading && farmSlides.length === 0);

  // 预加载当前与下一张，降低切图白屏
  useEffect(() => {
    if (!slides.length) return undefined;
    const urls = [slides[currentIndex], slides[(currentIndex + 1) % slides.length]]
      .map((s) => s?.coverUrl)
      .filter(Boolean);
    urls.forEach((u) => {
      const src = processImageUrl(u) || u;
      if (!src) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    });
    return undefined;
  }, [slides, currentIndex]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [slides.length, usingOperationCarousel, farmPreview?.hero?.farmId]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [slides.length]);

  const currentSlide = slides[currentIndex] || null;

  const goPrev = (e) => {
    e?.stopPropagation?.();
    if (!slides.length) return;
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };
  const goNext = (e) => {
    e?.stopPropagation?.();
    if (!slides.length) return;
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const handleSlideClick = () => {
    if (!currentSlide || currentSlide.kind !== 'operation') return;
    const url = currentSlide.linkUrl;
    if (!url) return;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      if (currentSlide.linkTarget === '_blank') {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
      return;
    }
    navigate(url.startsWith('/') ? url : `/${url}`);
  };

  /** 卡片仅展示静态封面；海康抓图在点进智能设备预览后再请求 */
  const openCameraHub = (deviceId, deviceLike) => {
    const lid =
      (deviceLike?.landId != null && String(deviceLike.landId).trim()) ||
      (deviceLike?.land_id != null && String(deviceLike.land_id).trim()) ||
      landIdProp ||
      '';
    const ch = Number(deviceLike?._channelNo);
    navigate(
      buildSmartDeviceUrl({
        farmId: activeFarmId,
        landId: lid || undefined,
        deviceId: deviceId || undefined,
        tab: deviceId ? SMART_DEVICE_TABS.PREVIEW : undefined,
        deviceType: TYPE_CAMERA,
        channelNo: Number.isFinite(ch) && ch > 0 ? ch : undefined,
      })
    );
  };

  const renderCameraThumb = (cam) => {
    const id = String(cam.id || '');
    const cardKey = String(cam._cardKey || id);
    const staticUrl = resolveStaticCover(cam);
    // 静态图仅绑定主通道，避免通道二套用通道一历史封面
    const ch =
      cam._channelNo != null && cam._channelNo > 0
        ? cam._channelNo
        : parseHikvisionConfig(cam).channelNo || 1;
    const allowStatic = !(isHikvisionCamera(cam) || isCameraConfigured(cam)) || ch === 1;
    const src = allowStatic ? staticUrl : '';
    const online = deviceOnline(cam);
    const channelLabel =
      cam._channelNo != null && cam._channelNo > 0 ? `通道 ${cam._channelNo}` : '';
    return (
      <button
        key={cardKey || deviceDisplayName(cam)}
        type="button"
        onClick={() => openCameraHub(id, cam)}
        className="group relative aspect-[16/10] rounded-lg overflow-hidden border border-gray-200 bg-slate-900 text-left focus:outline-none focus:ring-2 focus:ring-green-500/40"
      >
        {src ? (
          <img src={src} alt={deviceDisplayName(cam)} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-800">
            <VideoCameraIcon className="h-7 w-7 text-gray-400" aria-hidden />
            <span className="text-xs text-gray-300">点击查看画面</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
        <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
          <span className={`h-1.5 w-1.5 rounded-full ${online ? 'bg-emerald-400' : 'bg-gray-400'}`} />
          <span className="text-xs text-white font-medium">
            {online ? '在线' : '离线'}
          </span>
          {src ? (
            <span className="text-xs text-gray-200 ml-0.5">历史图</span>
          ) : null}
        </div>
        <div className="absolute bottom-1.5 left-1.5 right-1.5">
          <div className="text-xs text-white font-medium truncate">{deviceDisplayName(cam)}</div>
          <div className="text-xs text-gray-200 truncate">
            {[
              channelLabel,
              cam.installLocation || cam.landName || cam.landParcelName || '',
            ]
              .filter(Boolean)
              .join(' · ') || '田间固定机位'}
          </div>
        </div>
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 flex items-center justify-center">
          <span className="px-2 py-1 rounded bg-white text-xs text-gray-800 font-medium">查看监控</span>
        </div>
      </button>
    );
  };

  const farmFallbackSlide = farmSlides[0] || (hero ? heroToFarmSlide(hero) : null);
  const slideImage = currentSlide?.coverUrl ? processImageUrl(currentSlide.coverUrl) || currentSlide.coverUrl : '';
  const canClickSlide = currentSlide?.kind === 'operation' && Boolean(currentSlide.linkUrl);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
      <div className="px-4 py-3 flex items-center justify-between shrink-0">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <HomeModernIcon className="h-4 w-4 text-green-600" aria-hidden />
          <span>农场总览</span>
        </h3>
        {slides.length > 1 && (
          <span className="text-xs text-gray-600 tabular-nums">{currentIndex + 1}/{slides.length}</span>
        )}
      </div>

      {/* 轮播：沿用原运营位样式 — 模糊铺底 + 前景 object-contain 自适应 */}
      <div className="relative h-[220px] sm:h-[240px] shrink-0 bg-gray-100">
        {carouselBusy ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-3 border-green-200 border-t-green-600 rounded-full" />
          </div>
        ) : slides.length === 0 && !isDemo && !hasFarm ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
            <HomeModernIcon className="h-10 w-10 text-gray-300" aria-hidden />
            <p className="text-sm text-gray-600">{EMPTY_COPY.NO_RECORD}</p>
            <button
              type="button"
              onClick={() =>
                navigate(buildBusinessContextPath('/production/land', { farmId: activeFarmId, landId: landIdProp || '' }))
              }
              className="mt-1 px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
            >
              去创建农场
            </button>
          </div>
        ) : currentSlide ? (
          <>
            <button
              type="button"
              className={`absolute inset-0 block w-full h-full overflow-hidden ${canClickSlide ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={handleSlideClick}
              aria-label={currentSlide.name}
            >
              {slideImage ? (
                <div className="relative w-full h-full">
                  <img
                    src={slideImage}
                    alt=""
                    aria-hidden="true"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-60"
                  />
                  <div className="absolute inset-0 bg-black/10" />
                  <img
                    src={slideImage}
                    alt={currentSlide.name}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="relative z-10 w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.visibility = 'hidden';
                    }}
                  />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm bg-gradient-to-br from-gray-100 to-gray-200">
                  暂无图片
                </div>
              )}

              {/* 底部标题：轻量渐变，接近原运营位样式 */}
              {(currentSlide.name || currentSlide.subtitle) && (
                <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-none bg-gradient-to-t from-black/70 via-black/35 to-transparent px-4 pt-8 pb-3 text-white">
                  <div className="font-semibold text-sm truncate pr-16">{currentSlide.name}</div>
                  {currentSlide.subtitle ? (
                    <div className="text-xs text-white/85 truncate mt-0.5 pr-16">{currentSlide.subtitle}</div>
                  ) : currentSlide.kind === 'farm' ? (
                    <div className="text-xs text-white/80 truncate mt-0.5 pr-16">
                      {[currentSlide.area, currentSlide.crop, currentSlide.plots].filter((x) => x && x !== '—').join(' · ')}
                    </div>
                  ) : null}
                </div>
              )}
            </button>

            {usingOperationCarousel && farmFallbackSlide?.name ? (
              <div className="absolute top-2 right-2 z-30 px-2 py-0.5 rounded bg-black/50 text-xs text-white max-w-[42%] truncate pointer-events-none">
                {farmFallbackSlide.name}
              </div>
            ) : currentSlide.kind === 'farm' && currentSlide.status ? (
              <div className="absolute top-2 right-2 z-30 px-2 py-0.5 rounded-full bg-green-600 text-xs text-white font-medium pointer-events-none">
                {currentSlide.status}
              </div>
            ) : null}

            {slides.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="上一张"
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full bg-black/35 text-white hover:bg-black/55 text-lg leading-none flex items-center justify-center"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="下一张"
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full bg-black/35 text-white hover:bg-black/55 text-lg leading-none flex items-center justify-center"
                >
                  ›
                </button>
                <div className="absolute z-30 bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {slides.map((s, i) => (
                    <button
                      key={s.id || s.farmId || i}
                      type="button"
                      aria-label={`第${i + 1}张`}
                      onClick={() => setCurrentIndex(i)}
                      className={`h-2 rounded-full transition-all ${
                        i === currentIndex ? 'bg-white w-4' : 'bg-white/50 hover:bg-white/80 w-2'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : null}
      </div>

      {!carouselBusy && (hasFarm || isDemo) && (
        <div className="px-4 py-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <SignalIcon className="h-4 w-4 text-slate-600" aria-hidden />
              <span className="text-sm font-semibold text-gray-800">田间监控</span>
              {!cameraLoading && cameras.length > 0 && (
                <span className="text-xs text-gray-600">{cameras.length} 路</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => openCameraHub()}
              className="inline-flex items-center gap-0.5 text-sm text-gray-600 hover:text-green-700 font-medium"
            >
              全部监控
              <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>

          {cameraLoading ? (
            <div className="grid grid-cols-2 gap-3">
              {[0, 1].map((i) => (
                <div key={i} className="aspect-[16/10] rounded-lg bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : cameras.length > 0 ? (
            <div className={`grid gap-3 ${cameras.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {cameras.map(renderCameraThumb)}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => openCameraHub()}
              className="w-full rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center hover:border-green-400 hover:bg-green-50 transition-colors"
            >
              <VideoCameraIcon className="h-6 w-6 text-gray-400 mx-auto mb-1" aria-hidden />
              <div className="text-sm text-gray-700 font-medium">
                {isDemo ? '演示模式暂无摄像头' : '本农场暂无摄像头'}
              </div>
              <div className="text-xs text-gray-600 mt-1">前往智能设备接入监控</div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FarmOverviewCard;
